import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get("phone_alt") as string || "").trim();
    if (honeypotValue) {
      setFormStatus("success");
      setFormMessage("感谢您的留言，我们会尽快回复！");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "phone_alt") {
        payload.append(key, value as string);
      }
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/d9qac6me3oq4jqumtjag", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && (serverMsg.includes("spam") || serverMsg.includes("form data is spam"));

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setFormStatus("success");
        setFormMessage("感谢您的留言，我们会尽快回复！");
        form.reset();
      } else {
        setFormStatus("error");
        setFormMessage(typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后重试。");
      }
    } catch {
      setFormStatus("error");
      setFormMessage("提交失败，请检查网络后重试。");
    }
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SP完全指南",
    "url": "https://sp-guide.readdy.ai/",
    "description": "致力于传播科学、安全、理性的SP知识，帮助每一位读者在知情、自愿、安全的前提下获取正确信息。",
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "contact@sp-guide.com",
      "contactType": "customer service"
    }
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "关于我们 - SP完全指南",
    "description": "SP完全指南的品牌介绍、信誉展示和联系方式。致力于传播科学、安全、理性的SP知识。",
    "url": "https://sp-guide.readdy.ai/about",
    "inLanguage": "zh-CN"
  };

  return (
    <>
      <SEOHead
        title="关于我们 - SP完全指南"
        description="SP完全指南的品牌介绍、信誉展示和联系方式。致力于传播科学、安全、理性的SP知识，帮助读者在安全、知情、自愿的前提下获取正确的SP相关知识。"
        keywords="SP指南,关于我们,SP知识平台,联系我们"
        canonicalUrl="https://sp-guide.readdy.ai/about"
        ogTitle="关于我们 - SP完全指南"
        ogDescription="SP完全指南的品牌介绍、信誉展示和联系方式。"
      />
      <SchemaOrg data={orgSchema} />
      <SchemaOrg data={webPageSchema} />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: "关于我们" },
            ]}
          />

          {/* Hero */}
          <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20cozy%20reading%20room%20with%20soft%20golden%20morning%20light%2C%20cream%20walls%2C%20bookshelves%2C%20comfortable%20armchair%2C%20elegant%20atmosphere%2C%20editorial%20photography%20with%20warm%20amber%20tones%20and%20gentle%20shadows&width=1800&height=800&seq=about-hero-2026&orientation=landscape"
              alt="关于我们"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 w-full">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-3">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-background-50/80 max-w-lg">
                致力于传播科学、安全、理性的SP知识
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                品牌介绍
              </h2>
              <div className="prose prose-lg max-w-none prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base prose-p:mb-4">
                <p>
                  <strong>SP完全指南</strong>是一个专注于打屁股SP知识科普的中文平台。我们的使命是传播科学、安全、理性的SP知识，帮助每一位读者在知情、自愿、安全的前提下获取正确的信息。
                </p>
                <p>
                  我们深知，SP作为一个相对小众的话题，网络上充斥着大量零散、不准确甚至误导性的信息。因此，我们组建了专业的编辑团队，致力于为用户提供系统化、结构化的SP知识体系，涵盖从基础概念到高级技巧的完整学习路径。
                </p>
                <p>
                  我们的内容严格遵循<strong>安全、理智、自愿</strong>三大原则。每一篇文章都经过精心编写和审核，确保信息的准确性和实用性。我们相信，只有在正确的知识引导下，SP活动才能真正成为增进亲密关系的积极体验。
                </p>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
                我们的核心价值观
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: "ri-shield-check-line",
                    title: "安全第一",
                    desc: "安全是一切SP活动不可逾越的底线。我们从身体安全、心理安全到隐私安全，全方位关注每一位读者的安全需求。"
                  },
                  {
                    icon: "ri-hand-heart-line",
                    title: "知情同意",
                    desc: "我们倡导充分的知情和完全的自愿。每一场SP活动都应当建立在双方充分了解、明确同意的基础之上。"
                  },
                  {
                    icon: "ri-user-heart-line",
                    title: "尊重关怀",
                    desc: "SP的本质是亲密互动而非暴力伤害。我们强调尊重、关怀和事后护理，倡导健康积极的SP文化。"
                  }
                ].map((value, index) => (
                  <div key={index} className="bg-background-50 rounded-xl border border-background-200/60 p-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                      <i className={`${value.icon} text-primary-600 text-2xl`}></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">{value.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{value.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                信誉展示
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
                {[
                  { number: "150+", label: "原创文章" },
                  { number: "50,000+", label: "月度读者" },
                  { number: "6", label: "知识分类" },
                  { number: "99.8%", label: "内容好评率" }
                ].map((stat, index) => (
                  <div key={index} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-accent-500 mb-1">{stat.number}</div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
              <div className="prose prose-lg max-w-none prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base prose-p:mb-4">
                <p>
                  自平台建立以来，我们始终坚持<strong>内容为王、用户至上</strong>的理念。我们的编辑团队由具有丰富SP知识和经验的从业者组成，每一篇文章都经过严格的事实核查和专业审核。我们承诺不发布任何未经证实、可能误导读者的信息。
                </p>
                <p>
                  我们的读者遍布各个年龄段和背景，从完全不了解SP的新手到有多年经验的实践者，都能在我们的平台上找到有价值的内容。我们持续关注读者的反馈和建议，不断优化内容和用户体验。
                </p>
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 text-center">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                如果您有任何问题、建议或合作意向，欢迎通过以下表单与我们联系。我们会在48小时内回复。
              </p>

              <form
                onSubmit={handleSubmit}
                data-readdy-form=""
                className="bg-background-50 rounded-xl border border-background-200/60 p-6 md:p-8 flex flex-col gap-5"
              >
                {/* Honeypot */}
                <div className="contact-form-extra" aria-hidden="true">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" readOnly />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="name" className="text-sm font-medium text-foreground-700">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="text-sm font-medium text-foreground-700">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="subject" className="text-sm font-medium text-foreground-700">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                    placeholder="请输入留言主题"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="message" className="text-sm font-medium text-foreground-700">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors resize-none"
                    placeholder="请输入您的留言（限500字）"
                  ></textarea>
                  <span className="text-xs text-foreground-400 self-end">最多500字</span>
                </div>

                {formStatus !== "idle" && (
                  <div className={`px-4 py-3 rounded-lg text-sm ${
                    formStatus === "success"
                      ? "bg-accent-100 text-accent-700 border border-accent-200"
                      : "bg-accent-100 text-accent-700 border border-accent-200"
                  }`}>
                    {formMessage}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}